import * as signalR from "@microsoft/signalr";

const debugCallHub = (...args) => {
  if (import.meta.env?.DEV) {
    console.debug("[call-hub]", ...args);
  }
};

export const registerHandler = (connection, eventName, handler) => {
  if (!connection || !eventName || !handler) return () => {};
  debugCallHub("register handler", eventName, connection.connectionId);

  const safeHandler = (...args) => {
    try {
      const res = handler(...args);
      if (res && typeof res.then === "function") {
        res.catch((err) => {
          if (import.meta.env?.DEV) {
            console.warn(`[callHubClient] Error in async handler for '${eventName}':`, err);
          }
        });
      }
    } catch (err) {
      if (import.meta.env?.DEV) {
        console.warn(`[callHubClient] Error in sync handler for '${eventName}':`, err);
      }
    }
  };

  const lowerName = String(eventName).toLowerCase();
  const camelName = String(eventName)[0].toLowerCase() + String(eventName).slice(1);

  connection.off(eventName, safeHandler);
  connection.on(eventName, safeHandler);

  if (lowerName !== eventName) {
    connection.off(lowerName, safeHandler);
    connection.on(lowerName, safeHandler);
  }

  if (camelName !== eventName && camelName !== lowerName) {
    connection.off(camelName, safeHandler);
    connection.on(camelName, safeHandler);
  }

  return () => {
    debugCallHub("remove handler", eventName, connection.connectionId);
    connection.off(eventName, safeHandler);
    if (lowerName !== eventName) {
      connection.off(lowerName, safeHandler);
    }
    if (camelName !== eventName && camelName !== lowerName) {
      connection.off(camelName, safeHandler);
    }
  };
};

export const invokeCallHub = async (connection, methodName, ...args) => {
  if (!connection) {
    throw new Error("Call hub connection is not available.");
  }

  if (connection.state !== signalR.HubConnectionState.Connected) {
    throw new Error("Realtime connection is not ready. Please try again.");
  }

  debugCallHub("invoke", methodName, args);
  return connection.invoke(methodName, ...args);
};

export const createCallHubClient = ({
  getConnection,
  startConnection,
} = {}) => ({
  getConnection: (options) => {
    if (!getConnection) {
      throw new Error("getConnection is required for this call hub client.");
    }
    return getConnection(options);
  },
  startConnection: (options) => {
    if (!startConnection) {
      throw new Error("startConnection is required for this call hub client.");
    }
    return startConnection(options);
  },
  registerHandler: (eventName, handler) =>
    registerHandler(getConnection(), eventName, handler),
  registerConnectionHandler: (options, eventName, handler) =>
    registerHandler(getConnection(options), eventName, handler),
  invoke: (methodName, ...args) => invokeCallHub(getConnection(), methodName, ...args),
  invokeConnection: invokeCallHub,
});
